import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { AbstractControl, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { OptionalType } from '../../../models/types/optional.type';

@Component({
    selector: 'sl-datetime-input',
    templateUrl: './datetime-input.component.html',
    styleUrls: ['./datetime-input.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DatetimeInputComponent implements OnInit {
    @Input() placeholderKey: string;
    @Input() control: AbstractControl<OptionalType<Date> | null>;

    timeControl = new FormControl('', this.timeValidator);

    ngOnInit(): void {
        this.control.addValidators(this.dateTimeValidator(this.timeControl));
        if (this.control.hasValidator(Validators.required)) {
            this.timeControl.addValidators(Validators.required);
        }

        if (this.control.value) {
            const hours = this.control.value.getHours();
            const hoursStr = hours < 10 ? `0${hours}` : `${hours}`;
            const minutes = this.control.value.getMinutes();
            const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`;
            this.timeControl.setValue(`${hoursStr}:${minutesStr}`);
        } else if (this.timeControl.hasValidator(Validators.required)) {
            this.timeControl.setValue('00:00');
        }
    }

    onChange(): void {
        this.timeControl.updateValueAndValidity();
        this.control.updateValueAndValidity();

        const date = this.control.value;
        const time = this.timeControl.value;

        if (this.control.invalid || !date) {
            return;
        }

        if (!!time) {
            const [hours, minutes] = time.split(':').map(v => Number(v));
            date.setHours(hours);
            date.setMinutes(minutes);
        } else {
            date.setHours(0);
            date.setMinutes(0);
            this.timeControl.setValue('00:00');
        }

        this.control.setValue(date);
    }

    onBlur(): void {
        this.timeControl.markAsTouched();
        this.control.markAsTouched();
        this.onChange();
    }

    private timeValidator(control: AbstractControl<string | null>): ValidationErrors | null {
        const value = control.value;

        // Empty value is valid if field is not required
        if (!value && control.hasValidator(Validators.required)) {
            // eslint-disable-next-line no-null/no-null
            return null;
        }

        // Match (0)0:00 - 23:59
        const matches = value?.match(/^(?:[01]?[0-9]|2[0-3]):[0-5][0-9]$/);
        if (!matches) {
            return { invalidTime: true };
        }

        // eslint-disable-next-line no-null/no-null
        return null;
    }

    private dateTimeValidator(timeControl: AbstractControl<string | null>): ValidatorFn {
        return () => this.timeValidator(timeControl);
    }
}
